import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { HelpCircle, ArrowRight } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { SearchInput, AppButton, EmptyState, PageHeader } from "../ui";
import { priceLabel, isPaid } from "./marketplaceShared";
import "./Marketplace.scss";

/**
 * MarketplaceQuizBanksPage — all published quiz packs (library top folders) as
 * rows: name, question/topic counts, price, installed/update state. Click →
 * pack detail page (preview, topics, install).
 */
const MarketplaceQuizBanksPage = () => {
  const navigate = useNavigate();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const fetchTree = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/quiz-categories/marketplace-tree");
      setTree(data.tree || []);
    } catch { /* handled by empty state */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  const packs = useMemo(() => {
    let list = tree;
    if (typeFilter === "free") list = list.filter((p) => !isPaid(p.pricing));
    if (typeFilter === "paid") list = list.filter((p) => isPaid(p.pricing));
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(s) || (p.description || "").toLowerCase().includes(s));
    }
    return list;
  }, [tree, search, typeFilter]);

  return (
    <div className="mkp-root">
      {/* Title + subheading render in the TopNav via the PageHeader portal. */}
      <PageHeader
        icon={HelpCircle}
        backButton
        onBack={() => navigate("/marketplace")}
        title="Quiz Banks"
        description="MCQs and assessments for every subject — install a pack into your Question Bank"
      />

      <div className="mkp-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search quiz banks..." />
        <div className="mkp-pills">
          {["all", "free", "paid"].map((t) => (
            <button
              key={t}
              type="button"
              className={`mkp-pill ${typeFilter === t ? "mkp-pill--active" : ""}`}
              onClick={() => setTypeFilter(t)}
            >
              {t === "all" ? "All" : t === "free" ? "Free" : "Paid"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="mkp-loading"><Spinner animation="border" size="sm" /></div>
      ) : packs.length === 0 ? (
        <EmptyState
          icon={<HelpCircle size={40} />}
          title={search || typeFilter !== "all" ? "No matching packs" : "Nothing published yet"}
          description={
            search || typeFilter !== "all"
              ? "Try a different search or filter."
              : "No quiz packs have been published to the marketplace yet."
          }
        />
      ) : (
        <div className="mkp-rows">
          {packs.map((p) => (
            <div
              className="mkp-row"
              key={p._id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/marketplace/quiz-banks/${p._id}`)}
              onKeyDown={(e) => { if (e.key === "Enter") navigate(`/marketplace/quiz-banks/${p._id}`); }}
            >
              <span className="mkp-row-icon mkp-row-icon--purple">
                {p.icon || <HelpCircle size={20} />}
              </span>
              <div className="mkp-row-main">
                <div className="mkp-row-title">
                  {p.name}
                  <span className="mkp-chip mkp-chip--type">Quiz</span>
                  {p.installed?.updates > 0 && <span className="mkp-chip mkp-chip--update">Update available</span>}
                  {p.installed?.imported > 0 && p.installed?.updates === 0 && (
                    <span className="mkp-chip mkp-chip--installed">Installed</span>
                  )}
                </div>
                <div className="mkp-row-sub">
                  {p.totalCount} questions · {p.subfolders?.length || 0} topics
                  {p.description ? ` — ${p.description}` : ""}
                </div>
              </div>
              <div className="mkp-row-side">
                <span className={`mkp-price ${isPaid(p.pricing) ? "mkp-price--paid" : "mkp-price--free"}`}>
                  {priceLabel(p.pricing)}
                </span>
                <AppButton
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); navigate(`/marketplace/quiz-banks/${p._id}`); }}
                >
                  View <ArrowRight size={13} />
                </AppButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketplaceQuizBanksPage;
